import React, { useMemo } from 'react';

function startOfMonth(d) { const n = new Date(d.getFullYear(), d.getMonth(), 1); n.setHours(0,0,0,0); return n; }
function endOfMonth(d) { const n = new Date(d.getFullYear(), d.getMonth()+1, 0); n.setHours(0,0,0,0); return n; }
function formatYMD(d) { const yyyy=d.getFullYear(); const mm=String(d.getMonth()+1).padStart(2,'0'); const dd=String(d.getDate()).padStart(2,'0'); return `${yyyy}-${mm}-${dd}`; }

function getMonthMatrix(year, month){
  const first = new Date(year, month, 1); first.setHours(0,0,0,0);
  const startDow = first.getDay();
  const gridStart = new Date(first); gridStart.setDate(1 - startDow);
  const cells = [];
  for (let i=0;i<42;i++){
    const d = new Date(gridStart); d.setDate(gridStart.getDate()+i); d.setHours(0,0,0,0);
    cells.push(d);
  }
  return cells;
}

const weekdayLabels = ['일','월','화','수','목','금','토'];

export default function CalendarMonthGrid({
  months = [],
  holidayMap,
  getDayState,
  renderDayContent,
  onDayMouseDown,
  onDayMouseEnter,
  onDayMouseLeave,
  onDayClick,
  onBackgroundClick,
  getMonthSelectionState,
  onToggleMonth,
}) {
  const monthsNormalized = useMemo(() => {
    return (months || []).map((d) => startOfMonth(d));
  }, [months]);

  return (
    <div
      className="px-2 pb-3 flex-1 overflow-y-auto"
      onClick={(e) => {
        try {
          // If the click originated inside a day cell, ignore here
          const el = e.target;
          if (el && typeof el.closest === 'function' && el.closest('.ps-day-cell')) return;
        } catch (_) {}
        if (typeof onBackgroundClick === 'function') onBackgroundClick(e);
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
      {monthsNormalized.map((mDate) => {
        const year = mDate.getFullYear();
        const month = mDate.getMonth();
        const ymKey = `${year}-${String(month+1).padStart(2,'0')}`;
        const cells = getMonthMatrix(year, month);
        const selectionState = typeof getMonthSelectionState === 'function'
          ? getMonthSelectionState(year, month)
          : { checked: false, indeterminate: false };
        return (
          <section key={ymKey} className="p-3 bg-white mb-3 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <header
              className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3"
              onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}
            >
              {typeof onToggleMonth === 'function' && (
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={!!selectionState.checked}
                  ref={(el) => { if (el) el.indeterminate = !!selectionState.indeterminate && !selectionState.checked; }}
                  onMouseDown={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                  onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                  onChange={(e) => onToggleMonth(year, month, e.target.checked)}
                  title="이 달 전체 선택/해제"
                />
              )}
              <h3 className="text-base font-bold text-gray-900">{year}년 {month+1}월</h3>
            </header>
            {/* Weekday header row */}
            <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/50">
              {weekdayLabels.map((w, idx) => (
                <div 
                  key={w} 
                  className={`text-center py-2.5 text-sm font-semibold ${
                    idx === 0 ? 'text-emerald-500' : idx === 6 ? 'text-blue-500' : 'text-gray-600'
                  }`}
                >
                  {w}
                </div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="grid grid-cols-7 select-none">
              {cells.map((d, i) => {
                const key = formatYMD(d);
                const holidayName = holidayMap?.get ? holidayMap.get(key) : (holidayMap ? holidayMap[key] : undefined);
                const dow = d.getDay();
                const dowClass = (holidayName || dow===0) ? 'text-emerald-500' : (dow===6 ? 'text-blue-500' : 'text-gray-700');
                const state = typeof getDayState === 'function' ? getDayState(d, mDate) : {};
                
                // Calculate row and column for border styling
                const col = i % 7;
                const row = Math.floor(i / 7);
                
                if (state.isOther) {
                  return (
                    <div 
                      key={`${ymKey}-${i}`} 
                      className={`min-h-[100px] bg-gray-50/30 ${
                        col < 6 ? 'border-r border-gray-100' : ''
                      } ${row < 5 ? 'border-b border-gray-100' : ''}`}
                    />
                  );
                }

                const cellClasses = [
                  'ps-day-cell min-h-[100px] relative cursor-pointer transition-all duration-150 bg-white',
                  // Border styling
                  col < 6 ? 'border-r border-gray-100' : '',
                  row < 5 ? 'border-b border-gray-100' : '',
                  // Hover effect
                  state.filteredOut ? '' : 'hover:bg-blue-50/50',
                  // Selected state
                  state.isSelected && !state.filteredOut ? 'bg-blue-100/50 ring-2 ring-inset ring-blue-500' : '',
                  // Past dates
                  state.isPast ? 'opacity-60' : '',
                  // Closed
                  state.isClosed ? 'ps-closed' : '',
                  // Preview
                  state.inPreview ? 'ps-preview bg-emerald-50/50' : '',
                  // Drag selecting
                  state.inDrag ? 'ps-selecting bg-blue-100/70' : '',
                ].filter(Boolean).join(' ');

                return (
                  <div
                    key={`${ymKey}-${i}`}
                    className={cellClasses}
                    onMouseDown={(e) => onDayMouseDown && onDayMouseDown(d, e)}
                    onMouseEnter={(e) => onDayMouseEnter && onDayMouseEnter(d, e)}
                    onMouseLeave={(e) => onDayMouseLeave && onDayMouseLeave(d, e)}
                    onClick={(e) => onDayClick && onDayClick(d, e)}
                  >
                    {/* Date number - top left */}
                    <div className="flex items-start justify-between p-1.5">
                      <span className={`text-sm font-medium ${dowClass}`}>
                        {d.getDate()}
                      </span>
                      {/* Badge area - top right */}
                      {state.departureCount > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                          출발 {state.departureCount}
                        </span>
                      )}
                    </div>
                    
                    {/* Holiday indicator */}
                    {holidayName && (
                      <div className="absolute top-1.5 left-8 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                      </div>
                    )}
                    
                    {/* Closed badge */}
                    {state.isClosed && (
                      <span className="ps-closed-badge absolute bottom-4 left-1/2 transform -translate-x-1/2 text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                        마감
                      </span>
                    )}
                    
                    {/* Content area */}
                    <div className="px-1.5 pb-1.5 space-y-0.5 overflow-hidden">
                      {typeof renderDayContent === 'function' ? renderDayContent(d, { ...state, holidayName, dowClass }) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      </div>
    </div>
  );
}
